import { useState } from 'react';
import { ScreenLoading } from '@/components/loading';
import { ScreenError } from '@/components/screen-error';
import { ScreenPresentationLobby } from '@/components/screens/presentation-lobby-screen';
import { ScreenPresentation, useDeckFilters } from '@/components/screens/presentation-screen';
import { useApplications, useMe } from '@/hooks/use-applications';
import type { App } from '@/lib/catalog-config';
import type { ScreenProps } from '@/lib/theme';
import { useScreenProps } from '@/store/theme';

/**
 * Fluxo em dois atos: o LOBBY (chrome padrão, filtros no centro, botão
 * "Iniciar apresentação") e o DECK em tela cheia. Os filtros vivem AQUI — um
 * único `useDeckFilters` — então o recorte montado no lobby é exatamente o que
 * o deck projeta, e voltar do deck (X ou Esc) devolve ao lobby com tudo como
 * estava.
 */
function PresentationFlow({
  apps,
  role,
  sp,
}: Readonly<{ apps: App[]; role: 'admin' | 'collab'; sp: ScreenProps }>) {
  const filters = useDeckFilters(apps);
  const [started, setStarted] = useState(false);

  if (!started) {
    return (
      <div style={{ height: 'var(--app-vh, 100vh)', overflow: 'hidden' }}>
        <ScreenPresentationLobby
          {...sp}
          apps={apps}
          filters={filters}
          role={role}
          onStart={() => setStarted(true)}
        />
      </div>
    );
  }
  return (
    <ScreenPresentation
      apps={apps}
      filters={filters}
      autoFullscreen
      onExit={() => setStarted(false)}
    />
  );
}

/**
 * A lista vem do MESMO endpoint do catálogo, então o que cada pessoa apresenta
 * é exatamente o que ela pode ver: a API já filtra por visibilidade e acesso.
 */
export function PresentationPage() {
  const sp = useScreenProps();
  const { data: apps, isLoading, isError, error, refetch, isFetching } = useApplications();
  const { data: me } = useMe();
  const role = me?.role === 'admin' ? 'admin' : 'collab';

  if (isError && !apps) {
    return (
      <ScreenError
        error={error}
        onRetry={() => void refetch()}
        retrying={isFetching}
        active="presentation"
        role={role}
        title="Apresentação"
        subtitle="Deck do portfólio de plataformas"
      />
    );
  }
  if (isLoading || !apps) return <ScreenLoading dark={sp.dark} />;

  return <PresentationFlow apps={apps} role={role} sp={sp} />;
}
